/**
 * @description
 * Usage dashboard component showing detailed usage analytics and cost breakdowns.
 * Displays usage trends, service breakdowns, and cost projections.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-08
 * @see admin/src/frontend/components/stripe/UsageDashboard.test.tsx
 */

import { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import { api } from '../../services/api';

interface UsageData {
  date: string;
  anthropic: number;
  vibesdk: number;
  vscodeLens: number;
  total: number;
}

interface ServiceBreakdown {
  service: string;
  amount: number;
  percentage: number;
  trend: 'up' | 'down' | 'stable';
}

interface UsageDashboardProps {
  clientId: string;
}

/**
 * Usage dashboard component
 */
export function UsageDashboard({ clientId }: UsageDashboardProps) {
  const [usageData, setUsageData] = useState<UsageData[]>([]);
  const [breakdown, setBreakdown] = useState<ServiceBreakdown[]>([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d'>('30d');

  useEffect(() => {
    loadUsageData();
  }, [clientId, timeRange, loadUsageData]);

  const loadUsageData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Get real usage data from API
      const usageData = await api.getUsageData(clientId, timeRange);
      setUsageData(usageData);
      
      // Calculate breakdown from real data
      const breakdown: ServiceBreakdown[] = [
        {
          service: 'Anthropic API',
          amount: usageData.reduce((sum, day) => sum + day.anthropic, 0),
          percentage: 0,
          trend: 'up',
        },
        {
          service: 'VibeSDK',
          amount: usageData.reduce((sum, day) => sum + day.vibesdk, 0),
          percentage: 0,
          trend: 'stable',
        },
        {
          service: 'VSCode Lens',
          amount: usageData.reduce((sum, day) => sum + day.vscodeLens, 0),
          percentage: 0,
          trend: 'down',
        },
      ];

      // Calculate percentages
      const totalAmount = breakdown.reduce((sum, service) => sum + service.amount, 0);
      breakdown.forEach(service => {
        service.percentage = totalAmount > 0 ? (service.amount / totalAmount) * 100 : 0;
      });

      setBreakdown(breakdown);
    } catch (error) {
      console.error('Error loading usage data:', error);
      toast.error('Failed to load usage data');
      
      // Set empty data on error
      setUsageData([]);
      setBreakdown([]);
    } finally {
      setLoading(false);
    }
  }, [clientId, timeRange]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const getTrendIcon = (trend: 'up' | 'down' | 'stable') => {
    switch (trend) {
      case 'up':
        return <span className="text-green-600">↗</span>;
      case 'down':
        return <span className="text-red-600">↘</span>;
      case 'stable':
        return <span className="text-gray-600">→</span>;
    }
  };

  const getServiceColor = (service: string) => {
    switch (service) {
      case 'Anthropic API':
        return 'bg-blue-500';
      case 'VibeSDK':
        return 'bg-purple-500';
      case 'VSCode Lens':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };

  // Calculate summary stats
  const totalUsage = usageData.reduce((sum, day) => sum + day.total, 0);
  const averageDaily = totalUsage / usageData.length;
  const maxDaily = Math.max(...usageData.map(day => day.total));
  const minDaily = Math.min(...usageData.map(day => day.total));

  if (loading) {
    return (
      <div className="rounded-lg border bg-card p-6">
        <div className="flex items-center justify-center py-8">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-card p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold">Usage Dashboard</h3>
        <div className="flex gap-1">
          {(['7d', '30d', '90d'] as const).map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-3 py-1 text-sm rounded-md transition-colors ${
                timeRange === range
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-accent'
              }`}
            >
              {range}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid gap-4 md:grid-cols-4 mb-6">
        <div className="rounded-lg border bg-muted/50 p-4">
          <div className="text-sm text-muted-foreground">Total Usage</div>
          <div className="text-2xl font-bold">{formatCurrency(totalUsage)}</div>
          <div className="text-xs text-muted-foreground">Last {timeRange}</div>
        </div>
        <div className="rounded-lg border bg-muted/50 p-4">
          <div className="text-sm text-muted-foreground">Daily Average</div>
          <div className="text-2xl font-bold">{formatCurrency(averageDaily)}</div>
          <div className="text-xs text-muted-foreground">Per day</div>
        </div>
        <div className="rounded-lg border bg-muted/50 p-4">
          <div className="text-sm text-muted-foreground">Peak Day</div>
          <div className="text-2xl font-bold">{formatCurrency(maxDaily)}</div>
          <div className="text-xs text-muted-foreground">Highest usage</div>
        </div>
        <div className="rounded-lg border bg-muted/50 p-4">
          <div className="text-sm text-muted-foreground">Lowest Day</div>
          <div className="text-2xl font-bold">{formatCurrency(minDaily)}</div>
          <div className="text-xs text-muted-foreground">Minimal usage</div>
        </div>
      </div>

      {/* Service Breakdown */}
      <div className="mb-6">
        <h4 className="text-sm font-medium mb-3">Service Breakdown</h4>
        <div className="space-y-3">
          {breakdown.map((service) => (
            <div key={service.service} className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`h-3 w-3 rounded-full ${getServiceColor(service.service)}`}></div>
                <span className="text-sm font-medium">{service.service}</span>
                {getTrendIcon(service.trend)}
              </div>
              <div className="text-right">
                <div className="text-sm font-medium">{formatCurrency(service.amount)}</div>
                <div className="text-xs text-muted-foreground">{service.percentage.toFixed(1)}%</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Usage Chart */}
      <div className="mb-6">
        <h4 className="text-sm font-medium mb-3">Daily Usage Trend</h4>
        <div className="h-48 border rounded-md p-4 bg-muted/20">
          <div className="flex items-end justify-between h-full gap-1">
            {usageData.map((day, index) => {
              const maxValue = Math.max(...usageData.map(d => d.total));
              const height = (day.total / maxValue) * 100;
              
              return (
                <div key={index} className="flex-1 flex flex-col items-center">
                  <div 
                    className="w-full bg-primary rounded-t-sm transition-all duration-300 hover:bg-primary/80"
                    style={{ height: `${height}%` }}
                    title={`${formatDate(day.date)}: ${formatCurrency(day.total)}`}
                  ></div>
                  <div className="text-xs text-muted-foreground mt-1 transform -rotate-45 origin-top-left">
                    {formatDate(day.date)}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Recent Usage Table */}
      <div>
        <h4 className="text-sm font-medium mb-3">Recent Usage</h4>
        <div className="border rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr>
                <th className="text-left p-3">Date</th>
                <th className="text-right p-3">Anthropic</th>
                <th className="text-right p-3">VibeSDK</th>
                <th className="text-right p-3">VSCode Lens</th>
                <th className="text-right p-3">Total</th>
              </tr>
            </thead>
            <tbody>
              {usageData.slice(-7).map((day, index) => (
                <tr key={index} className="border-t hover:bg-muted/50">
                  <td className="p-3">{formatDate(day.date)}</td>
                  <td className="p-3 text-right">{formatCurrency(day.anthropic)}</td>
                  <td className="p-3 text-right">{formatCurrency(day.vibesdk)}</td>
                  <td className="p-3 text-right">{formatCurrency(day.vscodeLens)}</td>
                  <td className="p-3 text-right font-medium">{formatCurrency(day.total)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */
